import { QueryClient, QueryClientProvider } from "react-query";
import { shouldRetryQuery } from "./services/retryPolicy.js";
import Routes from "./routes.js";
import "./App.css";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { Client_App_ID } from "./utils/config.js";
import { ConditionalRecaptchaProvider } from "./contexts/RecaptchaContext.jsx";
import { ConsentProvider } from "./contexts/ConsentContext.jsx";
import { ColorProvider } from "./components/ContextApi.js";
import { GradientProvider } from "./components/GradientContext .js";
import { MyProvider } from "./components/ContextObject.js";
import { EditWheelProvider } from "./components/EditWheelProvider.js";
import { Provider } from "react-redux";
import { store } from "./redux/index.js";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import "react-toastify/dist/ReactToastify.css";
// F3 (probe-login-error-toast-contrast-reader-is-tool-broken-v2): overrides
// react-toastify's default error-toast background for AA contrast. Imported
// right after the library stylesheet it overrides, same reasoning as the two
// global stylesheets below it being imported last of all.
import "./styles/toastContrast.css";
// The app-wide keyboard focus indicator (ONE primitive). Imported LAST of the
// global stylesheets so it is also last in the emitted bundle — its (0,2,0)
// specificity already beats the component-local (0,1,0) rules, and being last
// removes the injection-order question entirely. See the file header for the
// measurements that motivated it.
import "./styles/focusRing.css";
// ISP-9999 (F1/F6, a11y-i18n-mobileweb) — the systemic prefers-reduced-motion
// catch-all. Imported LAST of the global stylesheets, same reasoning as
// focusRing.css above: `!important` already wins on specificity, but being
// last in the emitted bundle removes any injection-order question.
import "./styles/reducedMotion.css";
import { ToastContainer } from "react-toastify";
import { resetContainerProps } from "./redux/reducers/PrevStylesCopy/Container.js";
import { useEffect } from "react";
import CreateFolderDialog from "./components/WheelGallery/WheelListing/Folders/Components/CreateFolder.jsx";
import { WheelSegmentsProvider } from "./contexts/wheelSegments.jsx";
import { ThemeProvider } from "@mui/material/styles";
import { Muitheme } from "./utils/theme.js";
import DialogPopup from "./components/DialogPopup/index.jsx";
import UpgradePopup from "./components/Referrals/children/UpgradePopup.jsx";
import { BrowserRouter, useLocation } from "react-router-dom";
import NotificationHandler from "./utils/NotificationHandler";
import RouteAnnouncer from "./components/RouteAnnouncer";
import SkipToContent, { MAIN_CONTENT_ID } from "./components/SkipToContent";
import HelpAssistant from "./components/HelpAssistant";
import { HELP_ASSISTANT_ENABLED } from "./components/HelpAssistant/constants";
import I18nProvider from "./i18n/I18nProvider.jsx";
import CommandPaletteRoot from "./components/CommandPalette";
import { COMMAND_PALETTE_ENABLED } from "./constants/featureFlags";
import TermsAcceptanceGate from "./components/TermsAcceptanceGate";
import { TERMS_ACCEPTANCE_ENABLED } from "./components/TermsAcceptanceGate/featureFlag";
import UnverifiedBanner from "./components/UnverifiedBanner";
import { UNVERIFIED_GUARDRAILS_ENABLED } from "./constants/featureFlags";
import { isFixedViewportRoute } from "./fixedViewportRoute";
import { TOAST_CONTAINER_TOP } from "./utils/toastContainerPosition";

// A definitive client error (4xx) can never succeed on a retry, so we do NOT
// retry it — it surfaces immediately instead of hammering the endpoint 4x over
// ~7s behind a spinner (react-query's default was `retry: 3` for ALL failures).
// Transient failures (5xx / network) still recover. See services/retryPolicy.js
// (nemesis-0824-systemic-4xx-retry-storm).
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
  },
});

// ISP-9999 (white-bar-across-bottom): a separate component, not inline JSX in
// <App/>, because useLocation() only works inside <BrowserRouter> — and App's
// own render happens one level above that boundary.
const MainContent = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <div
      id={MAIN_CONTENT_ID}
      tabIndex={-1}
      style={{ outline: "none" }}
      data-fixed-viewport={isFixedViewportRoute(pathname) || undefined}
    >
      {children}
    </div>
  );
};

const App = () => {
  useEffect(() => {
    // Reset container properties when the app is loaded or refreshed
    store.dispatch(resetContainerProps());
  }, []);

  return (
    <I18nProvider>
    <BrowserRouter>
      <Provider store={store}>
        <ThemeProvider theme={Muitheme}>
          <DndProvider backend={HTML5Backend}>
            <MyProvider>
              <WheelSegmentsProvider>
                <GradientProvider>
                  <EditWheelProvider>
                    <ColorProvider>
                      <GoogleOAuthProvider clientId={Client_App_ID.GOOGLE_ID}>
                        <ConditionalRecaptchaProvider>
                          <ConsentProvider>
                            <div className="App">
                              {/* WCAG 2.4.1 (A) "Bypass Blocks": FIRST focusable
                                  thing in the document, so Tab reaches it before
                                  the toast container and the whole header. */}
                              <SkipToContent />
                              <ToastContainer
                                position="top-right"
                                autoClose={2000}
                                hideProgressBar={true}
                                newestOnTop={true}
                                theme="colored"
                                style={{
                                  zIndex: 999999999,
                                  top: TOAST_CONTAINER_TOP,
                                }}
                              />
                              <QueryClientProvider client={queryClient}>
                                <RouteAnnouncer />
                                <NotificationHandler />
                                {/* Skip-link target. A plain wrapper, NOT a
                                    <main> landmark: ViewWheelV2 and the two
                                    RendererV2 pages already render their own
                                    <main>, and an app-level one would nest.
                                    `.App` has no layout rules, so this extra
                                    block-level div is layout-neutral. */}
                                {UNVERIFIED_GUARDRAILS_ENABLED && <UnverifiedBanner />}
                                <MainContent>
                                  <Routes />
                                </MainContent>
                                {TERMS_ACCEPTANCE_ENABLED && <TermsAcceptanceGate />}
                                {COMMAND_PALETTE_ENABLED && <CommandPaletteRoot />}
                                <CreateFolderDialog />
                                <DialogPopup />
                                <UpgradePopup />
                                {HELP_ASSISTANT_ENABLED && <HelpAssistant />}
                              </QueryClientProvider>
                            </div>
                          </ConsentProvider>
                        </ConditionalRecaptchaProvider>
                      </GoogleOAuthProvider>
                    </ColorProvider>
                  </EditWheelProvider>
                </GradientProvider>
              </WheelSegmentsProvider>
            </MyProvider>
          </DndProvider>
        </ThemeProvider>
      </Provider>
    </BrowserRouter>
    </I18nProvider>
  );
};

export default App;
